import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, CheckCircle, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Define the ReferralData interface
interface ReferralData {
  title?: string;
  content?: string;
  recordedAt?: string;
  recipients?: string[] | any[];
  [key: string]: any; // Allow for additional properties
}

// Define the Item interface
interface ReferralItem {
  id?: string;
  sender?: string;
  message?: string;
  actionId?: number;
  details?:
    | ReferralData
    | {
        payload?: string;
        [key: string]: any;
      };
  isDraft?: boolean;
  displayTimestamp?: string;
  [key: string]: any;
}

const getReferralStatus = (status?: string) =>
  (status || "").trim().toLowerCase() === "sent";

// Helper function to format time
const formatTime = (timestamp: string) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ReferralItems = ({
  items,
  isDarkMode,
  onDelete,
  handleReferralClick,
  handleSendNowClick,
  isReferralSent,
}: any) => {
  // Sort items by timestamp (oldest first)
  const sortedItems = [...items].sort((a: ReferralItem, b: ReferralItem) => {
    const dateA = new Date(
      a.displayTimestamp || a.details?.recordedAt || 0,
    ).getTime();
    const dateB = new Date(
      b.displayTimestamp || b.details?.recordedAt || 0,
    ).getTime();
    return dateA - dateB;
  });

  return (
    <div className="space-y-3">
      {sortedItems.map((item: ReferralItem, index: number) => {
        // Parse details if they're in payload
        let referralData: ReferralData = {};

        try {
          if (
            item.details?.payload &&
            typeof item.details.payload === "string"
          ) {
            referralData = JSON.parse(item.details.payload) as ReferralData;
          } else {
            referralData = item.details || {};
          }
        } catch (e) {
          console.warn("Failed to parse referral payload", e);
          referralData = item.details || {};
        }
        const capitalize = (str: string) =>
          str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

        const title = referralData.title || "Consultation Referral";
        const recipientCount = referralData.recipients?.length || 0;
        const timestamp =
          item.displayTimestamp || referralData.recordedAt || "";
        const displayTime = formatTime(timestamp);
        const status = referralData.status || "Queued";
        const sent =
          getReferralStatus(status) || isReferralSent?.(item, referralData);
        return (
          <div
            key={item.id || index}
            className={`flex cursor-pointer items-start gap-2 p-2 rounded border transition-all duration-200 group`}
            onClick={() => handleReferralClick(referralData)}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <CheckCircle
                    className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                  <span
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    {referralData?.documentType === "email"
                      ? "Email"
                      : "Document"}
                  </span>
                  <Badge className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700">
                    {sent ? "Sent" : "Queued"}
                  </Badge>
                  {!sent && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendNowClick(item, referralData);
                      }}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded transition-colors ${
                        isDarkMode
                          ? "bg-blue-900/40 hover:bg-blue-900/60 text-blue-300"
                          : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                      }`}
                      title="Send referral email now"
                    >
                      <Send className="w-3 h-3" />
                      Send now
                    </button>
                  )}
                </div>
                {displayTime && (
                  <span
                    className={`text-xs ml-auto ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {displayTime}
                  </span>
                )}
              </div>

              <div className="ml-6 space-y-1">
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <span className="font-medium">Type:</span> {title}
                </p>

                {recipientCount > 0 && (
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Recipients: {recipientCount}{" "}
                    {recipientCount === 1 ? "email" : "emails"}
                  </p>
                )}
              </div>
            </div>

            {/* Delete button with AlertDialog */}
            <div
              className="items-center gap-1 transition-opacity duration-200 opacity-0 fflex group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    {/* <button
                      className={`p-1 rounded transition-colors ${
                        isDarkMode
                          ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                          : "hover:bg-red-50 text-red-600 hover:text-red-700"
                      }`}
                      title="Delete referral"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button> */}
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Referral</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this referral? This
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        // onClick={() => onDelete?.(item.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
